import { getAmplitudeInstance } from '../amplitudeHelpers';
import FilterName from '../enums/FilterName';
import MapBaseLayer from '../enums/MapBaseLayer';
import MapOverlay from '../enums/MapOverlay';
import PoiActionDetailedLocation from '../enums/PoiActionDetailedLocation';
import PoiInteraction from '../enums/PoiInteraction';
import PoiLocation from '../enums/PoiLocation';

type logPOISelectedParams = {
  collections_applied?: string[];
  collections_available?: string[];
  custom_route_id?: number;
  distance_from_poi_in_miles?: number;
  filters_applied?: FilterName[];
  is_recording?: boolean;
  lat?: number;
  lng?: number;
  map_base_layer?: MapBaseLayer;
  map_overlay?: MapOverlay[];
  method_of_interaction?: PoiInteraction;
  poi_detailed_location?: PoiActionDetailedLocation;
  poi_id?: number;
  poi_location?: PoiLocation;
  poi_type_raw?: string;
  route_loaded?: boolean;
  trail_id?: string;
};

/**
 * The user taps/swipes to select or switch the Poi.
 * @param params - Attributes to log with the amplitude event
 * @param params.collections_applied - The name for the collections that were set when an action took place.
 * @param params.collections_available - List of strings containing the collections available to the user.
 * @param params.custom_route_id - Fires if the user is selecting a POI while browsing a saved custom route
 * @param params.distance_from_poi_in_miles - The reported distance that the POI is. If in a pre-recording state, this corresponds to the distance from the start of the trail. If the user is in a recording state, this is the distance from the user's current location. For off-trail POIs, this is the distance as the crow flies from the start of the trail (pre-recording) or user position (recording).
 * @param params.filters_applied - List of strings containing the filters applied by the user
 * @param params.is_recording - Boolean indicator if the user was recording at the time the action took place.
 * @param params.lat - Map focus latitude
 * @param params.lng - Map focus longitude
 * @param params.map_base_layer - Name of tile base layer currently selected
 * @param params.map_overlay - The overlay currently applied to the map.
 * @param params.method_of_interaction - The method by which the user reached the POI (e.g. swipe or tap).
 * @param params.poi_detailed_location - Describes the detailed location where the poi selection action took place.
 * @param params.poi_id - Unique identifier of poi tapped
 * @param params.poi_location - Describes the location of the poi where the action took place e.g. explore map or navigator map.
 * @param params.poi_type - Describes the specific type of POI. Deprecated for poi_type_raw.
 * @param params.poi_type_raw - Describes the specific type of POI, based on a raw value of the POI type from the server.
 * @param params.route_loaded - Boolean indicator if a map was loaded upon entry to navigator.
 * @param params.trail_id - Trail id of the route displayed if viewing POI in navigator and route is loaded.
 */
const logPOISelected = (
  {
    collections_applied,
    collections_available,
    custom_route_id,
    distance_from_poi_in_miles,
    filters_applied,
    is_recording,
    lat,
    lng,
    map_base_layer,
    map_overlay,
    method_of_interaction,
    poi_detailed_location,
    poi_id,
    poi_location,
    poi_type_raw,
    route_loaded,
    trail_id
  }: logPOISelectedParams,
  callback?: () => void
) => {
  getAmplitudeInstance().logEvent(
    'poi selected',
    {
      'collections applied': collections_applied,
      'collections available': collections_available,
      'custom route id': custom_route_id,
      'distance from poi in miles': distance_from_poi_in_miles,
      'filters applied': filters_applied,
      'is recording': is_recording,
      lat,
      lng,
      'map base layer': map_base_layer,
      'map overlay': map_overlay,
      'method of interaction': method_of_interaction,
      'poi detailed location': poi_detailed_location,
      'poi id': poi_id,
      'poi location': poi_location,
      'poi type raw': poi_type_raw,
      'route loaded': route_loaded,
      'trail id': trail_id
    },
    callback
  );
};

export default logPOISelected;
